import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Expand, Search } from "lucide-react";
import { GALLERY, GALLERY_FILTERS } from "@/data/gallery";
import { GalleryLightbox } from "@/components/GalleryLightbox";
import { useGalleryStories } from "@/lib/galleryStories";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const AboutGallery = () => {
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState("");
  const stories = useGalleryStories();
  const [storyFirst, setStoryFirst] = useState(false);
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GALLERY.filter((g) => (filter === "All" || g.category === filter) && (!q || `${g.name} ${g.sub || ""} ${g.caption}`.toLowerCase().includes(q)));
  }, [filter, query]);

  return (
    <section id="gallery" className="mt-14 scroll-mt-24" data-testid="about-gallery">
      <h2 className="font-serif text-2xl">Moments from the journey</h2>
      <p className="mt-1 text-sm text-muted-foreground">A few faces from a lifetime in music. Tap any photo to view it full-size.</p>
      <div className="mt-5 flex flex-wrap gap-2" data-testid="gallery-filters">
        {GALLERY_FILTERS.map((f) => {
          const count = f === "All" ? GALLERY.length : GALLERY.filter((g) => g.category === f).length;
          const active = f === filter;
          return (
            <button
              key={f} onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/60 hover:text-foreground"}`}
              data-testid={`gallery-filter-${slug(f)}`}
            >
              {f} <span className="ml-1 opacity-70">{count}</span>
            </button>
          );
        })}
      </div>
      <div className="relative mt-4 max-w-sm" data-testid="gallery-search">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, e.g. Simon Cowell" aria-label="Search the gallery by name"
          className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none transition-colors focus:border-primary" data-testid="gallery-search-input" />
      </div>
      {!items.length && <p className="mt-6 text-sm text-muted-foreground" data-testid="gallery-no-results">No photos match “{query}”.</p>}
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((g, i) => (
          <motion.figure
            key={g.src} layout
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.6) }}
            onClick={() => { setStoryFirst(false); setOpen(i); }}
            className="group cursor-zoom-in overflow-hidden rounded-2xl border border-border bg-card"
            data-testid={`gallery-item-${i}`}
          >
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src={g.src} alt={g.caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><Expand size={14} /></span>
            </div>
            <figcaption className="px-3 py-2.5 text-center">
              <p className="text-xs font-extrabold uppercase tracking-wide text-foreground" data-testid={`gallery-name-${i}`}>{g.name}{g.sub && <span className="block">{g.sub}</span>}</p>
              {stories[g.src] && <button type="button" onClick={(e) => { e.stopPropagation(); setStoryFirst(true); setOpen(i); }} data-testid={`gallery-story-btn-${i}`}
                className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-bold text-primary hover:bg-primary/25"><BookOpen size={11} /> Tap to read story</button>}
            </figcaption>
          </motion.figure>
        ))}
      </div>
      <GalleryLightbox items={items} stories={stories} storyFirst={storyFirst} index={open} onClose={() => setOpen(null)} onIndex={setOpen} />
    </section>
  );
};
